import { formBuilderFromDocument } from "./generate-form-layout";
import { DocumentFormSource } from "./DocumentFormSource";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  assignGlobalProfileCollection,
  removeGlobalProfileAssignment,
  isGlobalProfileCollection,
  reusableFormSchema,
  suggestFormFromDocument,
  suggestFormMapping,
  formFieldCompatible,
  newField,
  uid,
  freeform,
  parseProject,
  type Field,
  type Project,
  type Resource,
} from "@proof/core";
import { useAuthoringServices } from "./services";
import { FormLibraryEditor } from "./LibraryEditors";
import { ProfileDatasetEditor } from "./ProfileDatasetEditor";
import {
  Button,
  Control,
  Modal,
  SetupModes,
  type SetupMode,
} from "./components";
import { Configuration } from "./Configuration";

/**
 * Full field settings for a profile type — the same inspector proofing forms
 * use. Profile inputs must stay independent, so document-only settings
 * (sections, calculations, conditions, presets, QR mappings) are cleared.
 */
export function ProfileFieldDesigner({
  name,
  tenantId,
  fields,
  onChange,
  notify,
}: {
  name: string;
  tenantId: string;
  fields: Field[];
  onChange(fields: Field[]): void;
  notify(message: string): void;
}) {
  const project = useMemo(
    () =>
      parseProject({
        ...freeform,
        id: "profile-type-fields",
        tenantId,
        name,
        fields,
        groups: [],
        tabs: [],
        parts: [],
        triggers: [],
        forms: [],
        formInstances: [],
        pages: freeform.pages.map((page) => ({ ...page, nodes: [] })),
      }),
    [fields, name, tenantId],
  );
  return (
    <Configuration
      fieldsOnly
      project={project}
      notify={notify}
      onChange={(next) =>
        onChange(
          next.fields.map((field) => ({
            ...field,
            source:
              field.source.kind === "literal"
                ? field.source
                : { kind: "literal" as const },
            calculation: undefined,
            groupId: undefined,
            visibleWhen: undefined,
            presets: [],
            ...(field.qr ? { qr: { ...field.qr, mapping: {} } } : {}),
          })) as Field[],
        )
      }
    />
  );
}

function Assignment({
  project,
  resource,
  onClose,
  onApply,
}: {
  project: Project;
  resource: Resource;
  onClose(): void;
  onApply(project: Project): void;
}) {
  const definition = reusableFormSchema.parse(resource.value);
  const suggested = useMemo(
    () => suggestFormMapping(definition.fields, project.fields),
    [resource, project],
  );
  const old = project.formInstances.find(
    (instance) =>
      instance.profileOnly &&
      project.forms.find((form) => form.id === instance.formId)?.profileLibrary
        ?.id === resource.id,
  );
  const [mapping, setMapping] = useState({
    ...suggested.mapping,
    ...Object.fromEntries(
      Object.entries(old?.mapping || {}).filter(([input, target]) =>
        definition.fields.some(
          (field) =>
            field.id === input &&
            project.fields.some(
              (f) => f.id === target && formFieldCompatible(field, f),
            ),
        ),
      ),
    ),
  });
  const [error, setError] = useState("");
  return (
    <Modal title="Assign global profile collection" wide onClose={onClose}>
      <p>
        {resource.name} · v{resource.version} → {project.name}. Map profile
        inputs to existing project variables. Unmapped values are not applied.
        This assignment leaves the document's form layout intact.
      </p>
      {definition.fields.map((input) => (
        <Control
          key={input.id}
          label={`Map profile ${input.label} to variable`}
          note={
            suggested.suggestions.find((item) => item.inputId === input.id)
              ?.reason
          }
        >
          <select
            value={mapping[input.id] || ""}
            onChange={(event) => {
              const next = { ...mapping };
              if (event.target.value) next[input.id] = event.target.value;
              else delete next[input.id];
              setMapping(next);
              setError("");
            }}
          >
            <option value="">Unmapped</option>
            {project.fields
              .filter((field) => formFieldCompatible(input, field))
              .map((field) => (
                <option key={field.id} value={field.id}>
                  {field.label} ({field.key})
                </option>
              ))}
          </select>
        </Control>
      ))}
      {error && <p role="alert">{error}</p>}
      <div className="button-row">
        <Button onClick={onClose}>Cancel assignment</Button>
        <Button
          disabled={!Object.keys(mapping).length}
          onClick={() => {
            try {
              onApply(
                assignGlobalProfileCollection(project, resource, mapping),
              );
            } catch (error) {
              setError((error as Error).message);
            }
          }}
        >
          Assign global profile
        </Button>
      </div>
    </Modal>
  );
}
export function GlobalProfiles({
  project,
  onChange,
  notify,
  canAssign = true,
}: {
  project: Project;
  onChange(project: Project): void;
  notify(message: string): void;
  canAssign?: boolean;
}) {
  const services = useAuthoringServices();
  const [datasetResource, setDatasetResource] = useState<Resource>();
  const [resources, setResources] = useState<Resource[]>([]),
    [search, setSearch] = useState("");
  const [draft, setDraft] =
      useState<ReturnType<typeof reusableFormSchema.parse>>(),
    [editing, setEditing] = useState<Resource>(),
    [fieldMode, setFieldMode] = useState<SetupMode>("guided"),
    [name, setName] = useState("");
  const [assignment, setAssignment] = useState<Resource>(),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const lifetime = useRef({
    alive: true,
    projectId: project.id,
    tenantId: project.tenantId,
  });
  lifetime.current.projectId = project.id;
  lifetime.current.tenantId = project.tenantId;
  useEffect(() => {
    lifetime.current.alive = true;
    return () => {
      lifetime.current.alive = false;
    };
  }, []);
  useEffect(() => {
    let active = true;
    setResources([]);
    void services
      .listResources?.()
      .then((rows) => {
        if (active)
          setResources(
            rows.filter(
              (row) =>
                row.tenantId === project.tenantId &&
                isGlobalProfileCollection(row),
            ),
          );
      })
      .catch((error) => {
        if (active) notify(error.message);
      });
    return () => {
      active = false;
    };
  }, [services.listResources, project.tenantId, project.id]);
  const rows = resources.filter(
    (row) =>
      !row.archived &&
      !resources.some(
        (other) => other.id === row.id && other.version > row.version,
      ),
  );
  const open = (row?: Resource) => {
    setEditing(row);
    setName(row?.name || "");
    setError("");
    setDraft(
      row
        ? reusableFormSchema.parse({
            ...(row.value as object),
            purpose: "profile-collection",
            application: "mapped",
          })
        : reusableFormSchema.parse({
            purpose: "profile-collection",
            application: "mapped",
            fields: [newField("text")],
          }),
    );
  };
  const save = async () => {
    if (!draft || !services.saveResource || busy) return;
    const projectId = project.id,
      tenantId = project.tenantId;
    setBusy(true);
    setError("");
    try {
      if (!name.trim())
        throw new Error("Enter a global profile collection name");
      const value = reusableFormSchema.parse({
        ...draft,
        purpose: "profile-collection",
        application: "mapped",
        profileSource: undefined,
      });
      const saved = await services.saveResource({
        id: editing?.id || uid("resource"),
        tenantId,
        kind: "form",
        name: name.trim(),
        version: editing?.version || 0,
        value,
      });
      if (
        !lifetime.current.alive ||
        lifetime.current.projectId !== projectId ||
        lifetime.current.tenantId !== tenantId
      )
        return;
      setResources((previous) => [...previous, saved]);
      setDraft(undefined);
      setEditing(undefined);
      notify(
        `Global profile collection ${saved.name} v${saved.version} saved. Existing assignments retain their saved versions.`,
      );
    } catch (error) {
      if (lifetime.current.alive) setError((error as Error).message);
    } finally {
      if (lifetime.current.alive) setBusy(false);
    }
  };
  return (
    <section aria-label="Global profile configuration" className="panel padded">
      <h2>{canAssign ? "Global profile types" : "Profile types"}</h2>
      <p>
        {canAssign
          ? "Map global profile types to this project's variables. People pick from their authorized profiles in the form; the first one fills it automatically."
          : "Each type is a set of fields — any input a proofing form supports. Types appear in the Profile type dropdown on Global profiles, where the profiles themselves are added and edited."}
      </p>
      {services.saveResource && (
        <div className="button-row">
          <Button onClick={() => open()}>
            Create global profile collection
          </Button>
        </div>
      )}
      <Control label="Search global profiles">
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </Control>
      {!rows.length && (
        <p>
          No global profile collections yet. Create a collection or save a
          reusable form in the shared library.
        </p>
      )}
      <div className="catalog-entries">
        {rows
          .filter((row) =>
            row.name.toLocaleLowerCase().includes(search.toLocaleLowerCase()),
          )
          .map((row) => (
            <article key={row.id} className="saved-proof-row">
              <div>
                <strong>
                  {row.name} · v{row.version}
                </strong>
                <small>
                  {reusableFormSchema.parse(row.value).fields.length} profile
                  inputs · Global collection
                </small>
              </div>
              <div className="button-row">
                <Button onClick={() => setDatasetResource(row)}>
                  Manage predefined dataset
                </Button>
                <Button
                  disabled={!canAssign}
                  onClick={() => setAssignment(row)}
                >
                  Assign to project
                </Button>
                {services.saveResource && (
                  <Button onClick={() => open(row)}>
                    Edit global collection
                  </Button>
                )}
              </div>
            </article>
          ))}
      </div>
      {canAssign && (
        <section aria-label="Assigned global profile collections">
          <h3>Assigned to {project.name}</h3>
          {project.formInstances
            .filter((instance) => instance.profileOnly)
            .map((instance) => {
              const form = project.forms.find(
                (form) => form.id === instance.formId,
              )!;
              return (
                <article key={instance.id} className="saved-proof-row">
                  <div>
                    <strong>
                      {instance.label} · v{form.profileLibrary?.version}
                    </strong>
                    <small>
                      {Object.keys(instance.mapping).length} mapped variables ·
                      Choose authorized predefined profiles in Live proof.
                    </small>
                  </div>
                  <div className="button-row">
                    <Button
                      onClick={() => {
                        const row = project.resourceSnapshots.find(
                          (row) =>
                            row.id === form.profileLibrary?.id &&
                            row.version === form.profileLibrary.version,
                        );
                        if (row) setAssignment(row);
                      }}
                    >
                      Review assignment
                    </Button>
                    <Button
                      onClick={() => {
                        onChange(
                          removeGlobalProfileAssignment(project, instance.id),
                        );
                        notify(
                          "Global profile assignment removed. Saved personal profiles are retained.",
                        );
                      }}
                    >
                      Remove assignment
                    </Button>
                  </div>
                </article>
              );
            })}
          {!project.formInstances.some((instance) => instance.profileOnly) && (
            <p>No global profile collections assigned to this project.</p>
          )}
        </section>
      )}
      {draft && (
        <Modal
          title={
            editing
              ? "Edit global profile collection"
              : "Create global profile collection"
          }
          wide
          onClose={() => {
            if (!busy) setDraft(undefined);
          }}
        >
          <fieldset
            disabled={busy}
            style={{ border: 0, padding: 0, minWidth: 0 }}
          >
            <Control label="Global profile collection name">
              <input
                maxLength={200}
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </Control>
            {!editing && (
              <DocumentFormSource
                project={project}
                label="Generate profile form from document"
                onSelect={(source, linked) => {
                  try {
                    const suggestion = suggestFormFromDocument(source);
                    setDraft({
                      ...suggestion.value,
                      purpose: "profile-collection",
                      application: "mapped",
                      extra_json_data: {
                        version: 1,
                        builder: formBuilderFromDocument(source),
                        linkedProjectIds: linked ? [source.id] : [],
                      },
                    });
                    if (!name.trim()) setName(`${source.name} profile`);
                    notify(
                      `Generated ${suggestion.value.fields.length} profile inputs for review.`,
                    );
                  } catch (error) {
                    setError((error as Error).message);
                  }
                }}
              />
            )}
            <SetupModes
              label="Profile field setup"
              guidedLabel="Quick setup"
              modes={["guided", "advanced"]}
              value={fieldMode}
              onChange={setFieldMode}
            />
            {fieldMode === "advanced" ? (
              <FormLibraryEditor
                profileCollection
                value={draft}
                onChange={(value) => setDraft(value as typeof draft)}
              />
            ) : (
              <ProfileFieldDesigner
                name={name || "Profile type"}
                tenantId={project.tenantId}
                fields={draft.fields}
                notify={notify}
                onChange={(fields) => setDraft({ ...draft, fields })}
              />
            )}
            {error && <p role="alert">{error}</p>}
            <div className="button-row">
              <Button disabled={busy} onClick={() => void save()}>
                Save global profile collection
              </Button>
              <Button disabled={busy} onClick={() => setDraft(undefined)}>
                Cancel
              </Button>
            </div>
          </fieldset>
        </Modal>
      )}
      {assignment && (
        <Assignment
          key={`${project.id}:${assignment.id}:${assignment.version}`}
          project={project}
          resource={assignment}
          onClose={() => setAssignment(undefined)}
          onApply={(next) => {
            onChange(next);
            setAssignment(undefined);
            notify(
              "Global profile collection assigned. Save the draft, then use Global profiles in Live proof.",
            );
          }}
        />
      )}
      {datasetResource && (
        <ProfileDatasetEditor
          resource={datasetResource}
          onClose={() => setDatasetResource(undefined)}
        />
      )}
    </section>
  );
}
